import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import ProductCard from "../components/ProductCard";

const FALLBACK_HERO = "https://images.unsplash.com/photo-1620916566398-39f1143ab7be?w=1600&q=85";

export default function Shop() {
  const { category } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [allCats, setAllCats] = useState([]);
  const [currentCat, setCurrentCat] = useState(null);
  const [sort, setSort] = useState(searchParams.get("sort") || "featured");
  const subcategory = searchParams.get("subcategory") || "";
  const search = searchParams.get("search") || "";

  useEffect(() => {
    api.get("/categories").then(r => {
      setAllCats(r.data || []);
      setCurrentCat((r.data || []).find(c => c.slug === category) || null);
    });
  }, [category]);

  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (category) params.set("category", category);
    if (subcategory) params.set("subcategory", subcategory);
    if (search) params.set("search", search);
    if (sort) params.set("sort", sort);
    params.set("limit", "500");
    api.get(`/products?${params.toString()}`)
      .then(r => setProducts(r.data))
      .finally(() => setLoading(false));
  }, [category, subcategory, search, sort]);

  const updateParam = (key, value) => {
    const p = new URLSearchParams(searchParams);
    if (value) p.set(key, value); else p.delete(key);
    setSearchParams(p);
  };

  // Shop "/shop" without category: show grid of all categories
  const showCategoryGrid = !category && !search;

  return (
    <div data-testid="shop-page" className="pt-28 min-h-screen">
      {/* Banner */}
      <section className="relative h-[40vh] md:h-[55vh] overflow-hidden">
        <img
          src={currentCat?.image || FALLBACK_HERO}
          alt={currentCat?.name || "Tienda"}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-carbon/60 via-carbon/40 to-carbon" />
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 h-full flex flex-col justify-end pb-12">
          <p className="overline mb-3">Boutique</p>
          <h1 className="font-heading text-5xl md:text-7xl text-ivory font-light tracking-tighter">
            {search ? `"${search}"` : (currentCat?.name || "Colecciones")}
          </h1>
          {currentCat?.description && !search && (
            <p className="text-ivory/70 mt-3 max-w-xl font-light">{currentCat.description}</p>
          )}
        </div>
      </section>

      {showCategoryGrid && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-16">
          <p className="overline mb-3">Explorar</p>
          <h2 className="font-heading text-3xl md:text-4xl text-ivory mb-10">Todas las categorías</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
            {allCats.map(c => (
              <Link
                key={c.slug}
                to={`/shop/${c.slug}`}
                data-testid={`grid-cat-${c.slug}`}
                className="group block aspect-[4/5] relative overflow-hidden bg-graphite border border-smoke cat-card"
              >
                <img src={c.image} alt={c.name} className="product-img absolute inset-0 w-full h-full object-cover opacity-70 group-hover:opacity-100 transition-opacity" />
                <div className="absolute inset-0 bg-gradient-to-t from-carbon via-carbon/30 to-transparent" />
                <div className="absolute bottom-0 left-0 right-0 p-5">
                  <p className="overline mb-1" style={{ fontSize: "12px" }}>{(c.subcategories || []).length} subcategorías</p>
                  <h3 className="font-heading text-2xl text-ivory group-hover:text-gold transition-colors">{c.name}</h3>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-12 md:py-20">
        {/* filters */}
        <div className="flex flex-col md:flex-row justify-between gap-6 mb-12 border-b border-smoke pb-6">
          <div className="flex flex-wrap gap-3" data-testid="subcategory-filters">
            {currentCat?.subcategories?.length > 0 && (
              <>
                <button
                  onClick={() => updateParam("subcategory", "")}
                  className={`text-xs uppercase tracking-[0.2em] px-4 py-2 border ${!subcategory ? "border-gold text-gold" : "border-smoke text-ivory/60 hover:text-gold hover:border-gold"} transition-colors`}
                >Todas</button>
                {currentCat.subcategories.map(sub => {
                  const slug = typeof sub === "string" ? sub : sub.slug;
                  const name = typeof sub === "string" ? sub : sub.name;
                  return (
                    <button
                      key={slug}
                      onClick={() => updateParam("subcategory", slug)}
                      data-testid={`subcat-${slug}`}
                      className={`text-xs uppercase tracking-[0.2em] px-4 py-2 border ${subcategory === slug ? "border-gold text-gold" : "border-smoke text-ivory/60 hover:text-gold hover:border-gold"} transition-colors`}
                    >{name}</button>
                  );
                })}
              </>
            )}
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs uppercase tracking-[0.2em] text-ivory/50">Ordenar</span>
            <select
              data-testid="sort-select"
              value={sort}
              onChange={e => setSort(e.target.value)}
              className="bg-transparent border border-smoke px-3 py-2 text-sm text-ivory uppercase tracking-wider focus:border-gold outline-none"
            >
              <option value="featured" className="bg-carbon">Destacados</option>
              <option value="newest" className="bg-carbon">Más nuevos</option>
              <option value="price-asc" className="bg-carbon">Precio ↑</option>
              <option value="price-desc" className="bg-carbon">Precio ↓</option>
              <option value="rating" className="bg-carbon">Mejor valorados</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="aspect-[3/4] shimmer" />
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-32">
            <p className="overline mb-4">Sin resultados</p>
            <p className="font-heading text-3xl text-ivory mb-6">No hemos encontrado productos</p>
            <Link to="/shop" className="btn-outline-gold inline-block">Volver al catálogo</Link>
          </div>
        ) : (
          <>
            <p className="text-xs text-ivory/40 uppercase tracking-wider mb-6">{products.length} producto{products.length !== 1 ? "s" : ""}</p>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
              {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
